import React, { useRef, useState } from 'react';
import { Button, Linking, Pressable, StyleSheet, Text, TextInput, View, } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import Config from 'react-native-config';
import { backendBaseUrlSelector, emailSelector, environmentSelector, setEmail, setCredentials, setEnvironmentConfig, setSelectedUserId, } from '../redux/appSlice';
import { applyEnvironmentForLogin, isSameWebSignupHost, resolveLoginEmailFromInput, resolveSignupTargetFromEmailInput, } from '../utils/resolveLoginEmailFromInput';
import { sanitizeSavedSigninEmail } from '../config/environment';
import { useLoginMutation } from '../services/abbiWayApi';
import usePasswordToggle from './usePasswordToggle';
import { reportServerError } from '../utils/reportServerError';
import { theme } from '../theme';
import { SIGNIN_NEW_USER_COPY_WEB, SIGNIN_SIGNUP_LINK_LABEL, } from '../config/storeMessaging';
const eyeColor = theme.colors.signInEyeWeb;
/**
 * Web sign-in: includes Forgot password (native uses SigninView.tsx without it until mobile flows ship).
 */
export const SigninView = () => {
    const email = useSelector(emailSelector);
    const backendBaseUrl = useSelector(backendBaseUrlSelector);
    const currentEnvironment = useSelector(environmentSelector);
    const debugPassword = __DEV__ ? Config.DEBUG_PASSWORD || '' : '';
    const [emailTyped, setEmailTyped] = useState(email);
    const [password, setPassword] = useState(debugPassword);
    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const [showPassword, PasswordToggleIcon] = usePasswordToggle(password.length !== 0, eyeColor, theme.colors.controlInactive);
    const dispatch = useDispatch();
    const navigation = useNavigation();
    const [login, { isLoading }] = useLoginMutation();
    const openSignup = () => {
        const target = resolveSignupTargetFromEmailInput(emailTyped, currentEnvironment);
        const currentHost = typeof window !== 'undefined' ? window.location?.hostname ?? '' : '';
        if (currentHost && isSameWebSignupHost(target.frontendBaseUrl, currentHost)) {
            navigation.navigate('Signup');
            return;
        }
        Linking.openURL(target.signupUrl);
    };
    const signin = async () => {
        const { email: loginEmail, environment } = resolveLoginEmailFromInput(emailTyped, currentEnvironment);
        const env = applyEnvironmentForLogin(environment);
        dispatch(setEnvironmentConfig({
            environment: env.environment,
            backendBaseUrl: env.backendBaseUrl,
            frontendBaseUrl: env.frontendBaseUrl,
        }));
        const savedEmail = sanitizeSavedSigninEmail(emailTyped);
        setEmailTyped(savedEmail);
        dispatch(setEmail(savedEmail));
        try {
            const result = await login({ email: loginEmail, password }).unwrap();
            dispatch(setCredentials(result));
            dispatch(setSelectedUserId(result.user.id));
        }
        catch (err) {
            reportServerError(err, {
                // Reuses the sign-up "Check your email" screen and its resend button.
                onResendConfirmation: () => navigation.navigate('PendingEmailConfirmation', { email: loginEmail }),
            });
        }
    };
    return (<View>
      <TextInput ref={emailRef} style={styles.emailInput} placeholder="email address" value={emailTyped} onChangeText={setEmailTyped} autoCapitalize="none" returnKeyType="next" onSubmitEditing={() => passwordRef.current?.focus()} blurOnSubmit={false}/>
      <View style={styles.row}>
        <TextInput ref={passwordRef} secureTextEntry={!showPassword} style={styles.passwordInput} placeholder="password" placeholderTextColor={theme.colors.textCaption} value={password} onChangeText={setPassword} autoCapitalize="none" returnKeyType="next" onSubmitEditing={() => emailRef.current?.focus()} blurOnSubmit={false}/>
        <PasswordToggleIcon />
      </View>
      <View style={styles.button}>
        <Button title={isLoading ? 'Signing in...' : 'Sign in'} disabled={!emailTyped.length || !password.length || isLoading} onPress={signin}/>
      </View>
      <View style={styles.signupBlock}>
        <Text style={styles.signupCopy}>{SIGNIN_NEW_USER_COPY_WEB}</Text>
        <Pressable onPress={openSignup} accessibilityRole="link" accessibilityLabel={SIGNIN_SIGNUP_LINK_LABEL}>
          <Text style={styles.signupLink}>{SIGNIN_SIGNUP_LINK_LABEL}</Text>
        </Pressable>
      </View>
      <Pressable style={styles.forgotRow} onPress={() => navigation.navigate('ForgotPassword', { emailHint: emailTyped })} accessibilityRole="button" accessibilityLabel="Forgot password">
        <Text style={styles.forgotLink}>Forgot password?</Text>
      </Pressable>
      {__DEV__ ? (<Text style={styles.debugText}>BACKEND: {backendBaseUrl}</Text>) : null}
    </View>);
};
const styles = StyleSheet.create({
    emailInput: {
        fontSize: 16,
        paddingHorizontal: 10,
        marginTop: 12,
        marginBottom: 12,
        height: 40,
        width: '100%',
        borderWidth: 1,
        borderRadius: 8,
        backgroundColor: theme.colors.surfaceElevated,
    },
    passwordInput: {
        flex: 1,
        fontSize: 16,
        paddingHorizontal: 10,
        marginTop: 12,
        marginBottom: 12,
        height: 40,
        borderWidth: 1,
        borderRadius: 8,
        backgroundColor: theme.colors.surfaceElevated,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        gap: 8,
    },
    icon: {
        marginLeft: 16,
    },
    button: {
        marginTop: 8,
        marginBottom: 28,
        marginLeft: 0,
        marginRight: 0,
        alignSelf: 'stretch',
    },
    signupBlock: {
        marginBottom: 24,
        alignSelf: 'stretch',
    },
    signupCopy: {
        fontSize: 16,
        lineHeight: 22,
        color: theme.colors.textOnPrimary,
        marginBottom: 10,
    },
    signupLink: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.textOnPrimary,
        textDecorationLine: 'underline',
    },
    forgotRow: {
        marginBottom: 8,
        alignSelf: 'flex-start',
    },
    forgotLink: {
        fontSize: 15,
        fontWeight: '600',
        color: theme.colors.textOnPrimary,
        textDecorationLine: 'underline',
    },
    debugText: {
        marginTop: 28,
        textAlign: 'left',
        fontSize: 12,
        color: theme.colors.textOnPrimary,
    },
});
